<template>
  <div class="drag-and-drop">
    <SlideUpDown :isActive="canAddMore" v-if="!isSmallBtn">
      <label class="drag-and-drop__wrap" :class="{ disabled: !canAddMore, 'hide-info': !showInfoDragAndDrop, 'new': newStyle, 'full-width': isFullWidth }">
        <input
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          multiple
          :accept="allowedTypes.join(',')"
          style="display: none"
        />
        <div class="drag-and-drop__wrap-col">
          <img src="/img/ui-kit/drag-drop-image.svg" alt="drag-drop">
          <div class="drag-and-drop__wrap-title"
            v-html="isImageProp ? $t('Drag Image or {tag}', { tag: '<span>' + $t('Browse') + '</span>' }) : $t('Drag file or {tag}', { tag: '<span>' + $t('Browse') + '</span>' })">
          </div>
        </div>
        <div v-if="isFullWidth" class="drag-and-drop__decor-line"></div>
        <div v-if="isFullWidth" class="drag-payout-form__info-text-block">
          <ul class="drag-payout-form__info-text">
            <li>
              <p>{{ $t("Accepted formats") }}:</p>
              <p class="mobile">{{formattedAllowedTypes}}</p>
            </li>
            <li>
              <p>{{ $t("Maximum upload file size:") }}:</p>
              <p class="mobile">{{`${maxSize} MB` }}</p>
            </li>
          </ul>
          <div class="drag-payout-form__info-text formats">
            <span>{{formattedAllowedTypes}}</span>
            <span>{{`${maxSize} MB` }}</span>
          </div>
        </div>
        <div v-else class="drag-and-drop__wrap-col" v-if="showInfoDragAndDrop">
          <ul class="drag-and-drop__wrap-params">
            <li><p>{{ $t("Document size should not exceed") }}:</p> <span>{{`${maxSize} MB` }}</span></li>
            <li><p>{{ $t("Valid file formats") }}:</p> <span>{{formattedAllowedTypes}}</span></li>
          </ul>
        </div>
      </label>
    </SlideUpDown>

    <div class="drag-and-drop__file-list" :class="{ 'with-files': pendingFiles.length > 0,'full-width': isFullWidth }">
      <div v-for="file in pendingFiles" :key="file.id" 
        class="drag-and-drop__file" :class="{ animate: file.visible, uploading: file.uploading, uploaded: file.uploaded }">
        <div class="drag-and-drop__file-item" >
          <div class="drag-and-drop__file-item-preview" :style="getPreviewStyle(file.name, file.preview)" :class="[getPreviewClass(file.name)]">
            <img v-if="isImageFile(file)" :src="file.preview" alt="preview"/>
          </div>

          <div class="drag-and-drop__file-item-info">
            <div class="file-name">{{ file.name }}</div>
            <div v-if="!file.uploaded" class="file-progress" role="progressbar" :aria-label="file.name"><span></span></div>
            <div v-else class="file-status"><svg class="wc-i" aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{{ $t("Uploaded") }}</div>
          </div>

          <button type="button" class="drag-and-drop__file-item-remove" @click.stop="removePendingFile(file)">
            <svg class="wc-i" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
          </button>
        </div>
      </div>
    </div>
    <label class="drag-and-drop__empty" v-if="isSmallBtn && !pendingFiles.length">
        <input
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          multiple
          :accept="allowedTypes.join(',')"
          style="display: none"
        />
        <span class="drag-and-drop__empty-icon">
          <svg class="wc-i" aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
        </span>
        <span class="drag-and-drop__empty-title">{{ $t('Drop files to attach') }}</span>
        <span class="drag-and-drop__hint">{{ $t('Up to {count} files, {mb} MB each', { count: maxFiles, mb: maxSize }) }} · {{ formattedAllowedTypes }}</span>
        <span class="drag-and-drop__empty-btn">{{ $t('Choose files') }}</span>
    </label>
    <div class="drag-and-drop__add-row" v-else-if="isSmallBtn">
    <label class="drag-and-drop__add-btn" :class="{ disabled: !canAddMore }">
        <input
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          multiple
          :accept="allowedTypes.join(',')"
          style="display: none"
        />
        <svg class="wc-i" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
        <span>{{ $t('Add more') }}</span>
      </label>
      <span class="drag-and-drop__hint">{{ formattedAllowedTypes }} · {{ $t('up to {mb} MB each', { mb: maxSize }) }}</span>
    </div>
    <p class="drag-and-drop__limit" role="alert" v-if="isSmallBtn && limitError"><svg class="wc-i" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>{{ $t('You can upload a maximum of {count} files.', { count: maxFiles }) }}</p>

    <div class="error-message" v-if="error">{{ error }}</div>
  </div>
</template>

<script>
import SlideUpDown from '/webpack-assets/vue/templates/chat/parts/ChatSlideUpDown.vue';
import { renderAlertVue } from '/webpack-assets/vue/utils/common.js';
export default {
  name: 'DragAndDrop',
  components: {
    SlideUpDown
  },
  props: {
    maxFiles: { 
        type: Number, 
        default: 5 
    },
    maxSize: { 
        type: Number, 
        default: 10 
    },
    isImageProp: {
      type: Boolean,
      default: true
    },
    showInfoDragAndDrop: {
      type: Boolean,
      default: true
    },
    useFileIcons: {
      type: Boolean,
      default: false
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    isSmallBtn: {
      type: Boolean,
      default: false
    },
    allowedTypes: {
      type: Array,
      default: () => ['image/jpeg', 'image/png', 'image/gif', 'application/pdf']
    },
    filesProp: {
      type: Array,
      default: () => []
    },
    allreadyUploadedFiles: {
      type: Array,
      default: () => []
    },
    newStyle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pendingFiles: [],
      isUploading: false,
      uploadProgress: 0,
      error: null,
      limitError: false,
      isDragging: false
    };
  },
  watch: {
    allreadyUploadedFiles: {
      handler(files) {
        if (!Array.isArray(files)) return;

        const mappedFiles = files.map(file => ({
          id: file.id,
          name: file.original_name,
          preview: file.preview_url || file.original_url,
          size: file.size,
          file: null,
          uploaded: true,
          uploading: false,
          uploadedId: file.id,
          visible: true
        }));

        this.pendingFiles = [...mappedFiles, ...this.pendingFiles];
        
        mappedFiles.forEach(file => {
          this.$emit('file-uploaded', file);
        })
      },
      immediate: true
    },
    filesProp: {
      immediate: true,
      handler(val) {
        if (val && val.length) {
          if (this.pendingFiles.length) return;
          this.addFiles(val);
        }
      }
    }
  },
  computed: {
    maxSizeBytes() {
      return (Number(this.maxSize) || 10) * 1024 * 1024;
    },
    canAddMore() {
      return this.pendingFiles.length < this.maxFiles;
    },
    formattedAllowedTypes() {
      const map = {
        'image/jpeg': 'JPEG',
        'image/jpg': 'JPEG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
        'image/webp': 'WebP',
        'image/avif': 'AVIF',
        'image/svg+xml': 'SVG',
        'image/bmp': 'BMP',
        'image/tiff': 'TIFF',
        'image/heic': 'HEIC',
        'image/heif': 'HEIF',

        'application/pdf': 'PDF',
        'application/msword': 'DOC',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
        'application/vnd.ms-excel': 'XLS',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
        'application/vnd.ms-powerpoint': 'PPT',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
        'text/plain': 'TXT',
        'text/csv': 'CSV',
        'application/rtf': 'RTF',

        'application/zip': 'ZIP',
        'multipart/x-zip': 'ZIP',
        'application/x-zip-compressed': 'ZIP',
        'application/x-rar-compressed': 'RAR',
        'application/x-7z-compressed': '7Z',

        'audio/mpeg': 'MP3',
        'audio/mp4': 'M4A',
        'audio/ogg': 'OGG',
        'audio/wav': 'WAV',
        'audio/aac': 'AAC',
        'audio/flac': 'FLAC',

        'video/mp4': 'MP4',
        'video/webm': 'WebM',
        'video/quicktime': 'MOV',
        'video/x-msvideo': 'AVI',
        'video/3gpp': '3GP',
        'video/x-matroska': 'MKV',

        'application/epub+zip': 'EPUB',
        'application/vnd.amazon.ebook': 'AZW',
        'application/vnd.oasis.opendocument.text': 'ODT',
        'application/vnd.oasis.opendocument.spreadsheet': 'ODS',
        'application/vnd.oasis.opendocument.presentation': 'ODP',
      };

      return [
        ...new Set(
          this.allowedTypes
            .map(type => {
              if (map[type]) return map[type];

              return type.split('/').pop()?.toUpperCase() || type;
            })
        )
      ].join(', ');
    }
  },
  methods: {
    handleDragOver(e) {
      e.preventDefault();
      if (this.canAddMore) this.isDragging = true;
    },
    handleDragLeave(e) {
      e.preventDefault();
      this.isDragging = false;
    },
    handleDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      if (this.canAddMore) this.addFiles(e.dataTransfer.files);
    },

    handleFileSelect(e) {
      if (this.canAddMore) this.addFiles(e.target.files);
      e.target.value = '';
    },
    addFiles(fileList) {
      const incomingFiles = Array.from(fileList);
      const freeSlots = this.maxFiles - this.pendingFiles.length;

      // if (freeSlots <= 0) {
      //   renderAlertVue(
      //     this.$t('Maximum files reached ({count})', { count: this.maxFiles }),
      //     'danger',
      //     2000
      //   );
      //   return;
      // }

      const filesToAdd = incomingFiles.slice(0, freeSlots);
      this.$emit('files-count', filesToAdd.length);

      filesToAdd.forEach(file => {
        const exists = this.pendingFiles.some(f =>
          f.file && f.file.name === file.name &&
          f.file.size === file.size &&
          f.file.lastModified === file.lastModified
        );

        if (exists) {
          renderAlertVue(this.$t('This file is already added!'), 'danger', 2000);
          this.$emit('files-count', filesToAdd.length - 1);
          return;
        }

        if (file.size === 0) {
          renderAlertVue(this.$t('Empty files are not allowed'), 'danger', 2000);
          this.$emit('files-count', filesToAdd.length - 1);
          return;
        }
        if (!this.allowedTypes.includes(file.type)) {
          renderAlertVue(this.$t('Invalid file type'), 'danger', 2000);
          this.$emit('files-count', filesToAdd.length - 1);
          return;
        }

        if (file.size > this.maxSizeBytes) {
          renderAlertVue(this.$t('The file size must not exceed {mb} MB', {mb: this.maxSize}), 'danger', 2000);
          this.$emit('files-count', filesToAdd.length - 1);
          return;
        }

        const fileEntry = {
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          preview: null,
          visible: false,
          uploading: false,
          uploaded: false,
          uploadedId: null
        };

        this.pendingFiles.push(fileEntry);
        this.$emit('file-uploaded', fileEntry);

        if (this.isImage(file.type)) {
          this.readFilePreview(file, fileEntry);
        }

        setTimeout(() => {
          fileEntry.visible = true;
          this.pendingFiles = this.pendingFiles.map(f =>
            f.id === fileEntry.id ? fileEntry : f
          );
          this.markFileUploading(fileEntry, 1500);
        }, 50);
      });

      if (incomingFiles.length > freeSlots) {
        // the chat dialog shows the limit inline (design); other contexts keep the toast
        if (this.isSmallBtn) {
          this.limitError = true;
        } else {
          renderAlertVue(
            this.$t('You can upload a maximum of {count} files.', { count: this.maxFiles }),
            'danger',
            2000
          );
        }
      }
    },
    isRealImage(file) {
      return new Promise(resolve => {
        const reader = new FileReader();

        reader.onloadend = () => {
          const arr = new Uint8Array(reader.result).subarray(0, 4);
          let header = '';
          for (let i = 0; i < arr.length; i++) {
            header += arr[i].toString(16);
          }

          const validHeaders = [
            '89504e47', // png
            'ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2', // jpg
            '47494638', // gif
            '52494646'  // webp
          ];

          resolve(validHeaders.some(h => header.startsWith(h)));
        };

        reader.onerror = () => resolve(false);
        reader.readAsArrayBuffer(file.slice(0, 4));
      });
    },
    markFileUploading(fileEntry, simulateDurationMs = 1000) {
      fileEntry.uploading = true;
      fileEntry.uploaded = false;
      this.pendingFiles = this.pendingFiles.map(f => f.id === fileEntry.id ? fileEntry : f);
      setTimeout(() => {
        fileEntry.uploading = false;
        //fileEntry.uploaded = true;
        this.pendingFiles = this.pendingFiles.map(f => f.id === fileEntry.id ? fileEntry : f);
      }, simulateDurationMs);
    },
    markFileUploaded(fileEntry) {
      fileEntry.uploaded = true;
      this.pendingFiles = this.pendingFiles.map(f => f.id === fileEntry.id ? fileEntry : f);
    },
    removePendingFile(fileToRemove) {
      const idx = this.pendingFiles.findIndex(f => f.id === fileToRemove.id);
      if (idx === -1) return;
      const file = this.pendingFiles[idx];
      this.limitError = false;
      file.visible = false;
      this.pendingFiles = this.pendingFiles.map(f => f.id === file.id ? file : f);
      this.$emit('file-deleted', file.uploadedId);
      setTimeout(() => {
        const remIdx = this.pendingFiles.findIndex(f => f.id === file.id);
        if (remIdx !== -1) this.pendingFiles.splice(remIdx, 1);
        this.pendingFiles = [...this.pendingFiles];
      }, 320);
    },
    isImage(type) {
      return type.startsWith('image/');
    },
    readFilePreview(file, fileEntry) {
      const reader = new FileReader();

      reader.onload = () => {
        fileEntry.preview = reader.result;
        this.pendingFiles = this.pendingFiles.map(f => f.id === fileEntry.id ? fileEntry : f);
      };

      reader.readAsDataURL(file);
    },
    getExtension(name) {
      return name.split('.').pop().toUpperCase();
    },
    truncateName(name, len = 20) {
      if (name.length <= len) return name;
      const ext = name.split('.').pop();
      return name.substr(0, len - ext.length - 3) + '...' + ext;
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    getPreviewClass(name) {
      const ext = (this.getExtension(name) || '').toLowerCase();

      const imageExt = ['jpg','jpeg','png','webp'];
      const allowed = ['pdf','rar','zip','7z','txt','csv','xls','xlsx'];
      const withIcon = ['jpg','jpeg','png','gif', ...allowed];
      const normalized = ext === 'jpeg' ? 'jpg' : ext;

      if (this.useFileIcons) {
        return withIcon.includes(ext) ? normalized : 'unknown';
      }

      if (imageExt.includes(ext)) {
        return 'img';
      }

      if (allowed.includes(ext)) {
        return normalized;
      }

      return 'unknown';
    },
    getPreviewStyle(name, preview) {
      const ext = (this.getExtension(name) || '').toLowerCase();
      const imageExt = ['jpg','jpeg','png','webp'];

      if (!this.useFileIcons && imageExt.includes(ext)) {
        return { backgroundImage: `url(${preview})` };
      }
    },
    isImageFile(file) {
      if (this.useFileIcons) {
        return false;
      }

      const ext = (this.getExtension(file.name) || '').toLowerCase();
      const imageExt = ['jpg','jpeg','png','gif','webp'];

      return imageExt.includes(ext) && !!file.preview;
    }
  },
  mounted() {
    this.$el.addEventListener('dragover', this.handleDragOver);
    this.$el.addEventListener('dragleave', this.handleDragLeave);
    this.$el.addEventListener('drop', this.handleDrop);
  },
  beforeUnmount() {
    this.$el.removeEventListener('dragover', this.handleDragOver);
    this.$el.removeEventListener('dragleave', this.handleDragLeave);
    this.$el.removeEventListener('drop', this.handleDrop);
  }
};
</script>

<style scoped lang="scss">
.drag-and-drop{
  &__wrap{
    padding: 20px 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    margin-bottom: 30px;
    cursor: pointer;
    transition: 0.4s;
    &.hide-info{
      min-height: 150px !important;
    }
    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
    &.full-width{
      min-height: 154px !important;
      flex-direction: row;
      margin-bottom: 0;
      @media only screen and (max-width: 767px) {
        flex-direction: column;
      }
      .drag-and-drop__wrap-col{
        width: 100%;
      }
      .drag-payout-form__info-text-block{
        display: flex;
        margin: 0;
        width: 100%;
      }
      .drag-payout-form__info-text{
        max-width: 80%;
        padding: 0 0 0 40px;
        @media only screen and (max-width: 992px) {
          max-width: 100%;
          padding: 0 20px 0 40px;
        }
        @media only screen and (max-width: 767px) {
          max-width: 100%;
          padding: 0;
        }
        &.formats{
          min-width: 130px;
          display: flex;
          flex-direction: column;
          @media only screen and (max-width: 767px) {
            display: none;
          }
          span{
            text-align: left;
            color: var(--wc-text);
            font-family: 'Roboto-Medium';
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 140%;
            margin-bottom: 10px;
            text-wrap: nowrap;
          }
        }
        li{
          align-items: flex-start;
          display: flex;
          padding-left: 18px;
          position: relative;
          width: 100%;
          margin-bottom: 10px;
          @media only screen and (max-width: 992px) {
            padding-left: 18px;
            flex-direction: row;
            align-items: flex-start;
          }
          &::before{
            background: var(--wc-accent);
            border-radius: 50%;
            content: "";
            height: 8px;
            left: 0;
            min-width: 8px;
            position: absolute;
            top: 6px;
            width: 8px;
            @media only screen and (max-width: 992px) {
              display: block;
            }
          }
          p{
            color: var(--wc-text-2);
            font-family: "Roboto-Regular";
            font-size: 14px;
            font-style: normal;
            line-height: 1.4285714286;
            margin-right: 20px;
            min-width: 165px;
            margin-bottom: 0;
            @media only screen and (max-width: 992px) {
              min-width: 163px;
              margin-right: 20px;
              margin-bottom: 5px;
            }
          }
          .mobile{
            text-wrap: wrap;
            display: none;
            color: var(--wc-text);
            font-family: 'Roboto-Medium';
            font-size: 14px;
            font-style: normal;
            line-height: 1.4285714286;
            @media only screen and (max-width: 767px) {
              display: block;
            }
          }
        }
      }
    }
    &-title{
      font-family: "Roboto-Regular", sans-serif;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0px;
      text-align: center;
      color: var(--wc-text);
      margin-top: 10px;
      :deep(span){
        color: var(--wc-accent);
        font-family: 'Roboto-Medium', sans-serif;
      }
    }
    &-col{
      display: flex;
      flex-direction: column;
      align-items: center;
      &:first-child{
        margin-bottom: 16px;
      }
    }
    &-params{
      list-style: none;
      padding: 0;
      margin: 0;
      li{
        display: flex;
        justify-content: center;
        gap: 4px;
        @media only screen and (max-width: 767px) {
          flex-direction: column;
          align-items: center;
        }
        p{
          font-family: "Roboto-Regular", sans-serif;
          font-weight: 100;
          font-size: 16px;
          line-height: 24px;
          color: var(--wc-text-3);
          margin: 0;
        }
        span{
          font-family: 'Roboto-Medium', sans-serif;
          font-weight: 100;
          font-size: 16px;
          line-height: 24px;
          color: var(--wc-text-3);
        }
      }
    }
  }
  &__file-list{
    //margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    transition: 0.4s;
    &.with-files{
      padding-bottom: 12px;
      &.full-width{
        padding-bottom: 0;
        padding-top: 15px;
        gap: 15px;
        justify-content: space-between;
        flex-direction: initial;
        flex-wrap: wrap;
        .drag-and-drop__file{
          width: calc(50% - 15px);
        }
      }
    }
  }
  // Chat attachments dialog opened from the paperclip before any file is picked
  &__empty{
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin: 0 0 12px;
    padding: 24px 16px;
    border: 1px dashed var(--wc-accent-border);
    border-radius: 12px;
    background: var(--wc-quote);
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
    &:hover{
      background: var(--wc-accent-soft);
      border-color: var(--wc-accent-2);
    }
    &-icon{
      width: 44px;
      height: 44px;
      margin-bottom: 4px;
      border-radius: 12px;
      background: var(--wc-surface);
      color: var(--wc-accent);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &-title{
      font-family: var(--wc-font-medium);
      font-size: 14px;
      line-height: 20px;
      color: var(--wc-text);
    }
    &-btn{
      margin-top: 8px;
      height: 36px;
      padding: 0 16px;
      border-radius: 8px;
      background: var(--wc-accent);
      color: var(--wc-on-accent);
      font-family: var(--wc-font-medium);
      font-size: 13px;
      line-height: 36px;
    }
  }
  &__add-row{
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    margin-bottom: 12px;
    .drag-and-drop__add-btn{
      margin-bottom: 0;
    }
  }
  &__hint{
    font-family: var(--wc-font);
    font-size: 12px;
    line-height: 16px;
    color: var(--wc-text-3);
  }
  &__limit{
    margin: 0 0 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 8px;
    background: var(--wc-danger-soft);
    color: var(--wc-danger);
    font-family: var(--wc-font);
    font-size: 13px;
    line-height: 18px;
  }
  &__add-btn{
    height: 36px;
    background-color: var(--wc-surface);
    border: 1px dashed var(--wc-accent-border);
    border-radius: 8px;
    gap: 6px;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    cursor: pointer;
    transition: 0.2s;
    color: var(--wc-accent);
    svg path{
      fill: currentColor;
    }
    &:hover{
      background-color: var(--wc-accent-soft);
    }
    &.disabled{
      border-color: var(--wc-border);
      color: var(--wc-text-4);
      pointer-events: none;
    }
    span{
      font-family: var(--wc-font-medium);
      font-weight: 400;
      font-size: 13px;
      line-height: 18px;
      color: currentColor;
    }
  }
  &__file{
    width: 100%;
    overflow: hidden;
    transition: 0.4s;
    height: 0;
    position: relative;
    &::after{
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      display: none;
      background: var(--wc-accent-2);

      transform: scaleX(0);
      transform-origin: left center;
      opacity: 0;

      transition: transform 1s linear;
      pointer-events: none;
    }
    &.animate{
      height: 56px;
    }
    &.uploading{
      .drag-and-drop__file-item{
        opacity: 0.5;
        pointer-events: none;
      }
      &::after{
        transform: scaleX(1);
        opacity: 1;
      }
    }
  }
  &__file-item{
    width: 100%;
    height: 56px;
    background: var(--wc-surface);
    border: 1px solid var(--wc-border);
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 4px;
    transition: 0.4s;
    &-preview {
      width: 46px;
      height: 46px;
      min-width: 46px;
      border-radius: 7px;
      overflow: hidden;
      padding: 0;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      background-image: url("/img/file-ext/jpg.svg");
      background-size: 46px;
      background-repeat: no-repeat;
      background-position: center center;
      &.jpg {
        background-image: url("/img/file-ext/jpg.svg");
      }
      &.png {
        background-image: url("/img/file-ext/png.svg");
      }
      &.pdf {
        background-image: url("/img/file-ext/pdf.svg");
      }
      &.csv {
        background-image: url("/img/file-ext/csv.svg");
      }
      &.gif {
        background-image: url("/img/file-ext/gif.svg");
      }
      &.rar {
        background-image: url("/img/file-ext/rar.svg");
      }
      &.txt {
        background-image: url("/img/file-ext/txt.svg");
      }
      &.xls {
        background-image: url("/img/file-ext/xls.svg");
      }
      &.xlsx {
        background-image: url("/img/file-ext/xlsx.svg");
      }
      &.zip {
        background-image: url("/img/file-ext/zip.svg");
      }
      &.img{
        background-size: cover;
        background-position: center;
      }
      .file-icon{
        text-align: center;
        font-family: 'Roboto-Medium', sans-serif;
      }
    }
    &-info {
      width: 100%;
      font-size: 12px;
      color: var(--wc-text-4);
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
      // Upload bar until the chat server confirms the file, then "Uploaded"
      .file-progress{
        height: 4px;
        border-radius: 2px;
        background: var(--wc-surface-3);
        overflow: hidden;
        span{
          display: block;
          height: 4px;
          width: 40%;
          border-radius: 2px;
          background: var(--wc-accent-2);
          animation: wc-file-progress 1.2s ease-in-out infinite;
        }
      }
      .file-status{
        display: flex;
        align-items: center;
        gap: 4px;
        font-family: var(--wc-font);
        font-size: 12px;
        line-height: 16px;
        color: var(--wc-success);
      }
      .file-name{
        font-family: var(--wc-font-medium);
        font-weight: 400;
        font-size: 13px;
        line-height: 18px;
        color: var(--wc-text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &-remove {
      width: 36px;
      height: 36px;
      min-width: 36px;
      border: 0;
      border-radius: 8px;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: 0.2s;
      opacity: 1;
      pointer-events: all;
      svg{
        width: 20px;
        height: 20px;
      }
      &:hover{
        background: var(--wc-danger-soft);
      }
      svg path{
        transition: 0.4s;
      }
      &:hover svg path{
        @media only screen and (min-width: 992px) {
          fill: var(--wc-danger);
        }
      }
    }
  }
}

.file-info {
  padding: 8px;
  font-size: 12px;
  color: var(--wc-text-4);
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0,0,0,0.6);
  border: none;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
}

.error-message {
  color: var(--wc-danger);
  margin-top: 10px;
}

// indeterminate: the upload API gives no progress events
@keyframes wc-file-progress {
  from { transform: translateX(-100%); }
  to { transform: translateX(250%); }
}
</style>
